import { sharedLib } from "@shared/services/shared.service";
import { useEffect, useState } from "react";
import { UUID_IN_USE_CODE } from "@shared/common/constants";
import { DisplayResultMessage, Platform } from "@shared/types";
import { useUser } from "@shared/utils/hooks/misc";
import { skipToken } from "@reduxjs/toolkit/query";

interface useSendAuthCodeProps {
  uuid?: string;
  shortUuid?: string;
  code?: string;
  displayResultMessage: DisplayResultMessage;
  platform: Platform;
  handleUuidInUse?: (response: any) => void;
}

const useSendAuthCode = ({
  uuid,
  shortUuid,
  code,
  displayResultMessage,
  platform,
  handleUuidInUse,
}: useSendAuthCodeProps) => {
  const [sendConnectToTelegramAuthCode, sendConnectToTelegramAuthCodeStatus] =
    sharedLib.sharedApi.authentication.useSendConnectToTelegramAuthCodeMutation();
  const { user } = useUser(true);
  const { data: metadata } = sharedLib.sharedApi.common.useGetMetadataQuery(
    user || !uuid ? skipToken : undefined
  );

  const validateAuthCode = (authCode: string | undefined) =>
    /^[0-9]{6}$/.test(authCode || "");
  const getCode = () => (validateAuthCode(code) ? code : undefined);
  const [authCode, setAuthCode] = useState<string | undefined>(getCode());
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [seconds, setSeconds] = useState<number>(0);
  const [invalidAuthCodeError, setInvalidAuthCodeError] = useState(false);

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      if (startDate) {
        const seconds =
          MIN_SECONDS_BETWEEN_SEND_AUTH_CODE -
          Math.round((new Date().getTime() - startDate?.getTime()) / 1000);
        if (seconds) setSeconds(seconds - 1);
        else if (seconds <= 0) {
          clearInterval(countdownInterval);
        }
      }
    }, 1000);
    return () => clearInterval(countdownInterval);
  }, [startDate]);

  useEffect(() => {
    setAuthCode(getCode());
  }, [code]);

  useEffect(() => {
    if (validateAuthCode(authCode) && invalidAuthCodeError) {
      setInvalidAuthCodeError(false);
    }
  }, [authCode]);

  const onSendAuthCodeClick = () => {
    if (uuid || shortUuid) {
      displayResultMessage(
        sendConnectToTelegramAuthCode({
          user_short_uuid: shortUuid,
          uuid: uuid!,
          platform,
        }).unwrap(),
        "Successfully sent the code via Telegram",
        undefined,
        undefined,
        undefined,
        [UUID_IN_USE_CODE]
      )
        .then(() => {
          setStartDate(new Date());
          setSeconds(MIN_SECONDS_BETWEEN_SEND_AUTH_CODE);
        })
        .catch(handleUuidInUse);
    }
  };

  const securityCodeInputData = {
    title: "Telegram Security Code",
    placeholder: "e.g. 926746",
    value: authCode,
    onChange: setAuthCode,
    description: invalidAuthCodeError
      ? "Please provide a valid 6-digit security code"
      : "6 digits security code sent from the ",
    isValid: !invalidAuthCodeError,
    showCodeSent: sendConnectToTelegramAuthCodeStatus.isLoading || !!seconds,
    codeSentTooltipText: `Didn't receive the code? Request new code in ${seconds} seconds. The code will expire in 10 minutes.`,
    showResendCode: sendConnectToTelegramAuthCodeStatus.isSuccess,
    botUrl: metadata?.bot_link ?? user?.bot_url,
    botName: "Cornix Trading Bot",
  };

  const isAuthCodeValid = validateAuthCode(authCode);

  return {
    validateAuthCode,
    authCode,
    setInvalidAuthCodeError,
    securityCodeInputData,
    onSendAuthCodeClick,
    isAuthCodeValid,
  };
};

export default useSendAuthCode;

const MIN_SECONDS_BETWEEN_SEND_AUTH_CODE = 20;
